import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useDeferredValue,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button as MuiButton,
  Chip,
  Checkbox,
  CircularProgress,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../components";
import { baseAxios } from "../../utils/config";
import { UserServiceForm } from "../../components/user-service-form/user-service-form";

const STATUS_CHIP_COLOR = {
  Active: "success",
  Inactive: "default",
};

const MARK_STYLE = {
  backgroundColor: "#fff882",
  color: "inherit",
  borderRadius: 4,
};

function HighlightText({ text, query }) {
  const str = String(text ?? "");
  const q = String(query ?? "").trim();
  if (!q || !str) return str || null;

  const tokens = q
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!tokens.length) return str;

  let pattern;
  try {
    pattern = new RegExp(`(${tokens.join("|")})`, "gi");
  } catch {
    return str;
  }

  const parts = str.split(pattern);
  if (parts.length === 1) return str;

  return parts.map((part, i) =>
    pattern.test(part) ? (
      <mark key={i} style={MARK_STYLE}>
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function HeaderColumnFilter({
  ariaLabel,
  value,
  onChange,
  anchorEl,
  setAnchorEl,
  options,
  allMenuLabel,
}) {
  const active = value !== "" && value != null;
  return (
    <Box
      component="span"
      onMouseDown={(e) => e.stopPropagation()}
      sx={{ display: "inline-flex", alignItems: "center", ml: 0.25 }}
    >
      <IconButton
        size="small"
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl((prev) =>
            prev === e.currentTarget ? null : e.currentTarget
          );
        }}
        color={active ? "primary" : "default"}
        sx={{
          p: 0.35,
          color: active ? "primary.main" : "rgba(255, 255, 255, 0.85)",
          opacity: active ? 1 : 0.85,
          "&:hover": {
            color: "common.white",
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
        }}
      >
        <FilterListIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { maxWidth: 360, mt: 0.5 },
          },
        }}
      >
        <MenuItem
          selected={value === ""}
          onClick={() => {
            onChange("");
            setAnchorEl(null);
          }}
        >
          {allMenuLabel}
        </MenuItem>
        {options.map((opt) => (
          <MenuItem
            key={String(opt.value)}
            selected={value === opt.value}
            onClick={() => {
              onChange(opt.value);
              setAnchorEl(null);
            }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

function serviceMatchesSearch(row, rawQuery) {
  const q = String(rawQuery ?? "").trim().toLowerCase();
  if (!q) return true;

  const blob = [
    row.service_name,
    row.service_shortcut,
    row.description,
    row.service_status,
  ]
    .map((x) => String(x ?? "").toLowerCase())
    .join(" ");

  const tokens = q.split(/\s+/).filter(Boolean);
  return tokens.every((tok) => blob.includes(tok));
}

function compareServiceRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  const sa = va == null ? "" : String(va);
  const sb = vb == null ? "" : String(vb);
  const primary = sa.localeCompare(sb, undefined, { sensitivity: "base" });
  if (primary !== 0) return primary;
  return (Number(a.service_id) || 0) - (Number(b.service_id) || 0);
}

function stylistDisplayName(stylist) {
  if (!stylist) return "stylist";
  const first = String(stylist.user_first_name ?? "").trim();
  const last = String(stylist.user_last_name ?? "").trim();
  if (first && last) return `${first} ${last}`;
  return first || last || "stylist";
}

export default function AddServiceToStylist() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const userIdParam = parseInt(queryParams.get("param"), 10);

  const [stylistInfo, setStylistInfo] = useState(null);
  const [availableServices, setAvailableServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configureOpen, setConfigureOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [servicesToAdd, setServicesToAdd] = useState([]);
  const [serviceErrors, setServiceErrors] = useState({});
  const [searchInput, setSearchInput] = useState("");
  const deferredSearch = useDeferredValue(searchInput);
  const [filterStatus, setFilterStatus] = useState("");
  const [statusFilterMenuAnchor, setStatusFilterMenuAnchor] = useState(null);
  const [sortBy, setSortBy] = useState("service_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const formData = useRef({});

  const stylistName = stylistDisplayName(stylistInfo);

  useEffect(() => {
    if (!Number.isFinite(userIdParam)) return;
    baseAxios
      .get(`stylists/${userIdParam}`)
      .then((res) => setStylistInfo(res.data))
      .catch((error) => {
        console.error("Error fetching stylist:", error);
        toast.error("Could not load stylist.", { duration: 3000 });
      });
  }, [userIdParam]);

  const loadAvailableServices = useCallback(() => {
    if (!Number.isFinite(userIdParam)) return;
    setLoading(true);
    baseAxios
      .get(`/services/getServicesToAdd/${userIdParam}`)
      .then((res) => {
        const data = res?.data;
        setAvailableServices(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setAvailableServices([]);
        toast.error("Could not load services.", { duration: 3000 });
      })
      .finally(() => setLoading(false));
  }, [userIdParam]);

  useEffect(() => {
    loadAvailableServices();
  }, [loadAvailableServices]);

  const statusFilterOptions = useMemo(() => {
    const set = new Set();
    for (const row of availableServices) {
      if (row.service_status) set.add(row.service_status);
    }
    return [...set].sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { sensitivity: "base" })
    );
  }, [availableServices]);

  const filteredServices = useMemo(() => {
    return availableServices.filter((row) => {
      if (!serviceMatchesSearch(row, deferredSearch)) return false;
      if (filterStatus && row.service_status !== filterStatus) return false;
      return true;
    });
  }, [availableServices, deferredSearch, filterStatus]);

  const sortedServices = useMemo(() => {
    const copy = [...filteredServices];
    copy.sort((a, b) => {
      const c = compareServiceRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [filteredServices, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const toggleSelected = useCallback((serviceId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(serviceId)) next.delete(serviceId);
      else next.add(serviceId);
      return next;
    });
  }, []);

  const toggleSelectAllFiltered = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const ids = filteredServices.map((s) => s.service_id);
      const allSelected = ids.length > 0 && ids.every((id) => next.has(id));
      if (allSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [filteredServices]);

  const filteredAllSelected =
    filteredServices.length > 0 &&
    filteredServices.every((s) => selectedIds.has(s.service_id));
  const filteredSomeSelected =
    filteredServices.some((s) => selectedIds.has(s.service_id)) &&
    !filteredAllSelected;

  const continueToConfigure = useCallback(() => {
    const selected = availableServices.filter((s) =>
      selectedIds.has(s.service_id)
    );
    if (selected.length < 1) {
      toast.error("Select at least one service.", { duration: 3000 });
      return;
    }
    selected.forEach((service) => {
      if (!formData.current[service.service_id]) {
        formData.current[service.service_id] = {};
      }
    });
    setServicesToAdd(selected);
    setServiceErrors({});
    setConfigureOpen(true);
  }, [availableServices, selectedIds]);

  const closeConfigureDialog = useCallback(() => {
    if (saving) return;
    setConfigureOpen(false);
  }, [saving]);

  const deleteService = useCallback((id) => {
    setServicesToAdd((prev) => {
      const next = prev.filter((x) => x.service_id !== id);
      if (next.length < 1) {
        setConfigureOpen(false);
      }
      return next;
    });
    delete formData.current[id];
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setServiceErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const saveAllServices = useCallback(async () => {
    const errors = {};

    for (const service of servicesToAdd) {
      const data = formData.current[service.service_id];
      const missing = [];

      const isFree =
        !service.service_price_level_1 &&
        !service.service_price_level_2 &&
        !service.service_price_level_3 &&
        !service.service_price_level_4 &&
        !service.service_price_level_5;

      if (!isFree && !data?.priceLevel) {
        missing.push("Price Level");
      }

      if (!data?.servicing || data.servicing < 15) {
        missing.push("Servicing Time");
      }

      if (missing.length > 0) {
        errors[service.service_id] = missing.join(", ");
      }
    }

    if (Object.keys(errors).length > 0) {
      setServiceErrors(errors);
      const errorServiceIds = Object.keys(errors);
      const firstName = servicesToAdd.find(
        (s) => String(s.service_id) === String(errorServiceIds[0])
      )?.service_name;
      const count = errorServiceIds.length;
      const message =
        count === 1
          ? `"${firstName}" is missing: ${errors[errorServiceIds[0]]}`
          : `"${firstName}" and ${count - 1} other service(s) have missing fields.`;
      toast.error(message, { duration: 4000 });
      return;
    }

    setServiceErrors({});
    setSaving(true);

    const payload = servicesToAdd.map((s) => {
      const data = formData.current[s.service_id] || {};
      return {
        userId: userIdParam,
        serviceId: s.service_id,
        priceLevel: data.priceLevel || 1,
        servicing: data.servicing,
        gap: data.gap || 0,
        servicing2: data.servicing2 || 0,
      };
    });

    try {
      const response = await baseAxios.post(
        "/services/addServicesToStylists",
        payload
      );
      if (response.status === 204) {
        toast.success("Services added.", { duration: 3000 });
        setConfigureOpen(false);
        navigate(-1);
      } else {
        toast.error("Unexpected response from server.", { duration: 3000 });
        setSaving(false);
      }
    } catch {
      toast.error("Error saving services.", { duration: 3000 });
      setSaving(false);
    }
  }, [navigate, servicesToAdd, userIdParam]);

  const columns = useMemo(() => {
    const sq = deferredSearch;
    return [
      {
        id: "select",
        label: (
          <Checkbox
            size="small"
            color="primary"
            checked={filteredAllSelected}
            indeterminate={filteredSomeSelected}
            onChange={toggleSelectAllFiltered}
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: "rgba(255,255,255,0.85)",
              "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                color: "primary.main",
              },
              p: 0.25,
            }}
          />
        ),
        sortable: false,
        minWidth: 56,
        width: 56,
        render: (_, row) => (
          <Checkbox
            size="small"
            color="primary"
            checked={selectedIds.has(row.service_id)}
            onChange={() => toggleSelected(row.service_id)}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      {
        id: "service_name",
        label: "Service Name",
        field: "service_name",
        minWidth: 180,
        render: (value) =>
          value ? <HighlightText text={value} query={sq} /> : "-",
      },
      {
        id: "service_shortcut",
        label: "Service Shortcut",
        field: "service_shortcut",
        minWidth: 140,
        render: (value) =>
          value ? <HighlightText text={value} query={sq} /> : "-",
      },
      {
        id: "description",
        label: "Description",
        field: "description",
        minWidth: 220,
        render: (value) =>
          value ? <HighlightText text={value} query={sq} /> : "-",
      },
      {
        id: "service_status",
        label: "Service Status",
        field: "service_status",
        minWidth: 140,
        headerExtras: (
          <HeaderColumnFilter
            ariaLabel="Filter by status"
            value={filterStatus}
            onChange={setFilterStatus}
            anchorEl={statusFilterMenuAnchor}
            setAnchorEl={setStatusFilterMenuAnchor}
            allMenuLabel="All statuses"
            options={statusFilterOptions.map((s) => ({
              value: s,
              label: s,
            }))}
          />
        ),
        render: (value) => {
          if (!value) return "-";
          return (
            <Chip
              size="small"
              variant="filled"
              label={value}
              color={STATUS_CHIP_COLOR[value] ?? "default"}
              sx={{ fontWeight: 500, lineHeight: 1 }}
            />
          );
        },
      },
    ];
  }, [
    deferredSearch,
    filterStatus,
    filteredAllSelected,
    filteredSomeSelected,
    selectedIds,
    statusFilterMenuAnchor,
    statusFilterOptions,
    toggleSelectAllFiltered,
    toggleSelected,
  ]);

  return (
    <Box sx={{ px: 8, py: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
            Add Services for {stylistName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select services to assign, then continue to set pricing and timing.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search name, shortcut, or description…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            variant="outlined"
            sx={{
              minWidth: 280,
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: "6px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                  <SearchIcon sx={{ fontSize: 20, color: "grey.400" }} />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end" sx={{ mr: 0.75 }}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    aria-label="Clear search"
                    sx={{
                      color: "grey.500",
                      "&:hover": {
                        color: "grey.800",
                        backgroundColor: "grey.200",
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton
            aria-label="Refresh available services"
            onClick={loadAvailableServices}
            disabled={loading}
            size="medium"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              borderRadius: "6px",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <MuiButton
            variant="contained"
            color="primary"
            onClick={continueToConfigure}
            disabled={selectedIds.size < 1}
            sx={{
              minHeight: 40,
              px: 2,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            <AddIcon sx={{ mr: 0.75 }} />
            Continue ({selectedIds.size})
          </MuiButton>
        </Box>
      </Box>

      <PaginatedTable
        columns={columns}
        rows={sortedServices}
        totalRowCount={sortedServices.length}
        loading={loading}
        onPaginationChange={noopPagination}
        onSortChange={handleSortChange}
        initialOrderBy="service_name"
        initialOrder="asc"
        searchQuery={`${deferredSearch}|${filterStatus}`}
        clientSidePagination
        skeletonHeight="40px"
      />

      <Dialog
        open={configureOpen}
        onClose={closeConfigureDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "min(92vh, 900px)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            bgcolor: "secondary.main",
            color: "common.white",
            borderBottom: "1px solid",
            borderColor: "primary.main",
            px: 3,
            py: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="inherit">
              Configure services
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.75)", mt: 0.25 }}
            >
              Set price level and timing for {stylistName} ({servicesToAdd.length}{" "}
              selected).
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={closeConfigureDialog}
            disabled={saving}
            size="small"
            sx={{
              color: "primary.main",
              mt: 0.25,
              "&:hover": { bgcolor: "rgba(197, 160, 111, 0.16)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            {servicesToAdd.map((service) => (
              <UserServiceForm
                key={service.service_id}
                service={service}
                formData={formData}
                loading={saving}
                deleteService={deleteService}
                hasError={!!serviceErrors[service.service_id]}
                onFieldChange={(id) =>
                  setServiceErrors((prev) => {
                    if (!prev[id]) return prev;
                    const next = { ...prev };
                    delete next[id];
                    return next;
                  })
                }
              />
            ))}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            gap: 1,
          }}
        >
          <MuiButton
            onClick={closeConfigureDialog}
            disabled={saving}
            variant="outlined"
            color="secondary"
            sx={{ textTransform: "none", minWidth: 100 }}
          >
            Cancel
          </MuiButton>
          <MuiButton
            variant="contained"
            color="primary"
            onClick={saveAllServices}
            disabled={saving || servicesToAdd.length < 1}
            sx={{
              minWidth: 140,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Save Services"
            )}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
